/**
 * OpenRouter to Unified AI Service Adapter
 * Maintains backward compatibility while using the new unified AI service
 */

import { StorySegment, ChatMessage, Archetype, UserProfile, CompanionMessage, StatChanges } from '../types';
import { unifiedAIService } from './unifiedAiService';
import { databaseService } from './databaseService';
import { DEFAULT_PLAYER_NAME } from '@/constants';
import { GameState } from '../types/gameTypes';

interface OpenRouterStoryResponse {
  narrative: string;
  companion_dialogue: CompanionMessage[];
  choices: string[];
  image_prompt: string;
  stat_changes?: StatChanges;
}

interface OpenRouterChatResponseFormat {
  dialogues: CompanionMessage[];
  stat_changes?: StatChanges;
}

/**
 * Generate a chat response using ElevenLabs agents for text + TTS
 */
export async function generateChatResponse(
  userMessage: string,
  chatHistory: any[],
  currentStorySegment: StorySegment,
  archetype: Archetype,
  userProfile: UserProfile | null,
  userId?: string
): Promise<OpenRouterChatResponseFormat> {
  try {
    console.log('[Adapter] Generating chat response via OpenRouter');

    // Use OpenRouter for text generation directly
    const { generateChatResponse: openRouterGenerateChat } = await import('./openRouterService');
    
    const context = `
Player: ${userProfile?.username || DEFAULT_PLAYER_NAME} (${archetype.name})
Current Scene: ${currentStorySegment.narrative}
Health: ${userProfile?.health || 100}, Energy: ${userProfile?.energy || 100}, Credits: ${userProfile?.credits || 100}
Recent History: ${chatHistory.slice(-3).map(h => h.content || h.text).join(' ')}`;

    // Call OpenRouter directly for chat response
    const response = await openRouterGenerateChat(
      userMessage,
      chatHistory,
      currentStorySegment,
      archetype,
      userProfile,
      userId
    );

    // Format response from OpenRouter
    const dialogues: CompanionMessage[] = response.dialogues || [{
      speaker: 'Game Master',
      message: 'Neural pathways stabilized. Ready to continue.',
      emotion: 'neutral'
    }];

    // Use stat changes from OpenRouter response
    const stat_changes: StatChanges = response.stat_changes || {
      health: 0,
      energy: Math.random() > 0.7 ? -5 : 0,
      credits: 0
    };

    return {
      dialogues,
      stat_changes
    };
  } catch (error) {
    console.error('[Adapter] Chat generation error:', error);
    return {
      dialogues: [{
        speaker: 'System',
        message: 'Neural link disrupted. Reconnecting...',
        emotion: 'error'
      }],
      stat_changes: { health: 0, energy: 0, credits: 0 }
    };
  }
}

/**
 * Generate a story segment using ElevenLabs agents for text + TTS
 */
export async function generateStorySegment(
  currentNarrative: string,
  playerChoice: string,
  gameHistorySummary: string,
  archetypeDescription: string,
  userProfile: UserProfile | null,
  userId?: string
): Promise<OpenRouterStoryResponse> {
  try {
    console.log('[Adapter] 📖 Generating story segment via OpenRouter');
    console.log('[Adapter] Current narrative:', currentNarrative?.substring(0, 100) + '...');
    console.log('[Adapter] Player choice:', playerChoice);
    console.log('[Adapter] Archetype:', archetypeDescription);

    // Build proper parameters for OpenRouter
    const archetype: Archetype = {
      name: archetypeDescription.split(' ')[0] || 'Hacker',
      description: archetypeDescription,
      stats: { strength: 10, agility: 10, intelligence: 10 },
      abilities: []
    };
    
    const previousChoices = [playerChoice];
    const sessionId = userId || 'default-session';
    
    // Use OpenRouter for text generation - calling the correct function
    const { generateStorySegment: openRouterGenerateStory } = await import('./openRouterService');
    const response = await openRouterGenerateStory(
      currentNarrative,
      playerChoice,
      gameHistorySummary,
      archetypeDescription,
      userProfile,
      userId
    );

    // Use the response from OpenRouter which already has the proper structure
    const narrative = response.narrative || 'The city pulses with digital life.';
    const choices = response.choices?.length >= 3 ? response.choices : [
      'Investigate the data stream',
      'Follow the neon trail', 
      'Access the terminal'
    ];

    // Generate scene image (non-blocking to prevent hanging)
    const imagePrompt = `cyberpunk scene: ${narrative.substring(0, 100)}`;
    let imageUrl = null;
    try {
      // Add timeout to prevent hanging
      const imagePromise = unifiedAIService.generateImage(imagePrompt);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Image generation timeout')), 5000)
      );
      imageUrl = await Promise.race([imagePromise, timeoutPromise]);
      console.log('[Adapter] Image generated successfully:', imageUrl);
    } catch (error) {
      console.warn('[Adapter] Image generation failed/timed out, continuing without image:', error);
      imageUrl = null; // Continue without image
    }

    // Use companion dialogue from OpenRouter response and ensure proper format
    console.log('[Adapter] Raw companion dialogue from OpenRouter:', response.companion_dialogue);
    const companion_dialogue: CompanionMessage[] = response.companion_dialogue?.map(msg => ({
      name: msg.name || msg.speaker || 'JAX',
      dialogue: msg.dialogue || msg.message || msg.text || '',
      speaker: msg.speaker || msg.name || 'JAX',
      message: msg.message || msg.dialogue || msg.text || '',
      emotion: msg.emotion || 'neutral'
    })).filter(msg => 
      // Ensure message has actual content
      (msg.dialogue && msg.dialogue.trim()) || 
      (msg.message && msg.message.trim())
    ) || [];
    console.log('[Adapter] Processed companion dialogue:', companion_dialogue);

    // Calculate stat changes
    const stat_changes: StatChanges = {
      health: playerChoice.toLowerCase().includes('fight') ? -10 : 0,
      energy: -5,
      credits: Math.random() > 0.8 ? 10 : 0
    };

    return {
      narrative,
      companion_dialogue,
      choices,
      image_prompt: imagePrompt,
      imageUrl,
      stat_changes
    };
  } catch (error) {
    console.error('[Adapter] Story generation error:', error);
    // Enhanced fallback with more detailed narrative
    const fallbackNarrative = `Neural static crackles through your consciousness as the digital veil flickers. The chrome and glass towers of Neo-Veridia stretch endlessly upward, their surfaces alive with cascading data streams and holographic advertisements that speak in languages both familiar and alien. 

Your augmented reality interface struggles to process the overwhelming sensory input – neon signs pulse in synesthetic waves of color and sound, while the distant hum of hover traffic creates a constant electronic symphony. In this moment between choices, you feel the weight of the megacity's collective consciousness pressing against your own neural barriers.

The air itself seems charged with possibility, thick with the electromagnetic whispers of a million connected minds navigating their own digital labyrinths. Your next decision will ripple through the network of cause and effect that binds this cyber-reality together.`;

    return {
      narrative: fallbackNarrative,
      companion_dialogue: [
        { name: "JAX", dialogue: "Neural pathways are stabilizing, but I'm detecting some interference in the local data grid. Stay alert." },
        { name: "Lyra", dialogue: "The digital currents are turbulent here. I sense deeper patterns moving beneath the surface chaos." }
      ],
      choices: [
        'Interface directly with the nearest data terminal to gather intelligence',
        'Navigate through the crowded neon district using stealth protocols',
        'Attempt to contact your network handler through encrypted channels'
      ],
      image_prompt: 'cyberpunk megacity street scene with neon lights, data streams, holographic displays, chrome towers',
      stat_changes: { health: 0, energy: -5, credits: 0 }
    };
  }
}

/**
 * Generate companion dialogue using ElevenLabs agents
 */
export async function generateCompanionDialogue(
  companionName: string,
  companionPersonality: string,
  currentContext: string
): Promise<CompanionMessage> {
  try {
    console.log('[Adapter] Generating companion dialogue via ElevenLabs agent');

    // Use ElevenLabs NPC agent for dialogue generation + TTS
    const { elevenLabsService } = await import('./elevenlabsService');
    const response = await elevenLabsService.generateWithAgent('npc', currentContext, `NPC Type: ${companionPersonality}`);

    return {
      speaker: companionName,
      message: response.text,
      emotion: 'neutral'
    };
  } catch (error) {
    console.error('[Adapter] Companion dialogue error:', error);
    return {
      speaker: companionName,
      message: 'Systems nominal. Ready for action.',
      emotion: 'neutral'
    };
  }
}

/**
 * Generate combat narrative using ElevenLabs agents
 */
export async function generateCombatNarrative(
  action: string,
  playerHealth: number,
  enemyHealth: number
): Promise<string> {
  try {
    console.log('[Adapter] Generating combat narrative via ElevenLabs agent');

    const combatContext = `Combat Action: ${action}
Player Health: ${playerHealth}/100
Enemy Health: ${enemyHealth}/100
Current Turn: 1
Player Turn: true

Describe the combat action result.`;

    // Use ElevenLabs combat agent for narrative generation + TTS
    const { elevenLabsService } = await import('./elevenlabsService');
    const response = await elevenLabsService.generateWithAgent('combat', combatContext);

    return response.text;
  } catch (error) {
    console.error('[Adapter] Combat narrative error:', error);
    return 'Your attack connects with digital precision!';
  }
}

/**
 * Generate image using Unified AI Service
 */
export async function generateImage(
  prompt: string,
  style: string = 'cyberpunk'
): Promise<string | null> {
  try {
    console.log('[Adapter] Generating image via UnifiedAI');
    return await unifiedAIService.generateImage(`${style} style, ${prompt}`);
  } catch (error) {
    console.error('[Adapter] Image generation error:', error);
    return null;
  }
}

/**
 * Text-to-Speech using ElevenLabs agents
 */
export async function textToSpeech(text: string): Promise<string | null> {
  try {
    console.log('[Adapter] Generating TTS via ElevenLabs agent');
    // Use narrator agent for TTS
    const { elevenLabsService } = await import('./elevenlabsService');
    const response = await elevenLabsService.generateWithAgent('narrator', text);
    return response.audioUrl || null;
  } catch (error) {
    console.error('[Adapter] TTS error:', error);
    return null;
  }
}

/**
 * Speech-to-Text using Web Speech API
 */
export async function speechToText(): Promise<string> {
  try {
    console.log('[Adapter] Starting STT via browser Web Speech API');
    // Use browser's native Web Speech API for STT
    return await unifiedAIService.speechToText();
  } catch (error) {
    console.error('[Adapter] STT error:', error);
    return '';
  }
}

/**
 * Fill user profile placeholders in text
 */
export function fillUserProfilePlaceholders(
  text: string, 
  userProfile?: UserProfile | null
): string {
  if (!userProfile) return text;
  
  return text
    .replace(/{{username}}/g, userProfile.username || DEFAULT_PLAYER_NAME)
    .replace(/{{health}}/g, String(userProfile.health || 100))
    .replace(/{{energy}}/g, String(userProfile.energy || 100))
    .replace(/{{credits}}/g, String(userProfile.credits || 100))
    .replace(/{{level}}/g, String(userProfile.level || 1));
}

// Export service for direct access if needed
export { unifiedAIService };